// Motor de física: porte do synapse-os/src/engine/physics.js para TS.
// Mudança: Coulomb usa Barnes-Hut (O(n log n)) em vez de O(n²).
// Comportamento preservado: spring com plastic deformation >450px, gravidade
// hierárquica por tamanho, drag elástico, gravidade central com breathing motion.

import { applyCoulomb, buildQuadtree } from './quadtree.js';
import type { DragState, GraphLink, GraphNode, PhysicsConfig } from './types.js';
import { DEFAULT_PHYSICS } from './types.js';

const DT = 0.016;

export interface PhysicsState {
  nodes: GraphNode[];
  links: GraphLink[];
  drag: DragState | null;
  time: number;
  searchQuery: string;
  focusedParentId: string | null;
  physicsEnabled: boolean;
}

export function getVisibleNodes(state: PhysicsState): GraphNode[] {
  const { nodes, searchQuery, focusedParentId } = state;
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    return nodes.filter(
      (n) =>
        n.label.toLowerCase().includes(q) ||
        (n.content ?? '').replace(/<[^>]+>/g, '').toLowerCase().includes(q),
    );
  }
  return nodes.filter((n) =>
    focusedParentId === null ? n.parentId == null : n.parentId === focusedParentId,
  );
}

export function tickPhysics(state: PhysicsState, config: PhysicsConfig = DEFAULT_PHYSICS): void {
  if (!state.physicsEnabled) return;
  const visible = getVisibleNodes(state);
  if (visible.length === 0) return;

  // 0. Âncoras: primeira passagem define home; teleporte externo (undo, snap,
  // colaborador remoto) re-ancora pra não puxar o nó de volta pra posição velha.
  const TELEPORT_SQ = 30 * 30;
  for (const nd of visible) {
    if (nd.homeX === undefined || nd.homeY === undefined) {
      nd.homeX = nd.x;
      nd.homeY = nd.y;
    } else if (nd.prevX !== undefined && nd.prevY !== undefined) {
      const dx = nd.x - nd.prevX;
      const dy = nd.y - nd.prevY;
      if (dx * dx + dy * dy > TELEPORT_SQ) {
        nd.homeX = nd.x;
        nd.homeY = nd.y;
      }
    }
  }

  // Nós sem nenhum link ficam parados: só participam da física como massa
  // (outros repelem deles), mas não recebem força nem integração.
  const linked = new Set<string>();
  for (const lk of state.links) {
    linked.add(lk.source);
    linked.add(lk.target);
  }

  // 1. Coulomb com Barnes-Hut.
  const tree = buildQuadtree(visible);
  for (const node of visible) {
    if (node.locked || !linked.has(node.id)) continue;
    const { ax, ay } = applyCoulomb(
      tree,
      node,
      config.coulombStrength,
      config.coulombMaxForce,
      config.barnesHutTheta,
      DT,
    );
    node.vx += ax;
    node.vy += ay;
  }

  // Index para acelerar lookups por id nos links.
  const byId = new Map<string, GraphNode>();
  for (const n of visible) byId.set(n.id, n);

  // 2. Spring forces com plastic deformation.
  for (const lk of state.links) {
    const a = byId.get(lk.source);
    const b = byId.get(lk.target);
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.hypot(dx, dy) || 1;
    let f = (dist - config.springRest) * config.springK;
    if (dist > config.plasticThreshold) {
      f = (config.plasticThreshold - config.springRest) * config.springK * (config.plasticThreshold / dist);
    }
    const fx = (dx / dist) * f;
    const fy = (dy / dist) * f;
    if (!a.locked) {
      a.vx += fx;
      a.vy += fy;
    }
    if (!b.locked) {
      b.vx -= fx;
      b.vy -= fy;
    }
  }

  // 3. Gravidade hierárquica por massa (size).
  const isDragging = state.drag && state.drag.moved;
  for (const lk of state.links) {
    const a = byId.get(lk.source);
    const b = byId.get(lk.target);
    if (!a || !b) continue;
    const massA = Math.max(a.size, 10);
    const massB = Math.max(b.size, 10);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const distSq = dx * dx + dy * dy + 1;
    const dist = Math.sqrt(distSq);

    // Elastic chiclete pulse — slight time-modulated stretch so attraction breathes.
    const chiclete = 1 + 0.22 * Math.sin(state.time * 3.4 + (a.x + b.y) * 0.004);
    if (massA > massB && !b.locked) {
      const massDiff = massA - massB;
      const pullRadius = massA * 18;
      if (dist < pullRadius) {
        const distRatio = 1 - dist / pullRadius;
        const strength = (massDiff / distSq) * 0.024 * distRatio * chiclete;
        b.vx += (dx / dist) * strength;
        b.vy += (dy / dist) * strength;
      }
    } else if (massB > massA && !a.locked) {
      const massDiff = massB - massA;
      const pullRadius = massB * 18;
      if (dist < pullRadius) {
        const distRatio = 1 - dist / pullRadius;
        const strength = (massDiff / distSq) * 0.024 * distRatio * chiclete;
        a.vx -= (dx / dist) * strength;
        a.vy -= (dy / dist) * strength;
      }
    }
  }

  // 3b. Drag-induced pull.
  if (isDragging && state.drag) {
    const dragged = byId.get(state.drag.id);
    if (dragged) {
      const draggedMass = Math.max(dragged.size, 10);
      for (const lk of state.links) {
        let other: GraphNode | undefined;
        if (lk.source === state.drag.id) other = byId.get(lk.target);
        else if (lk.target === state.drag.id) other = byId.get(lk.source);
        if (!other || other.locked) continue;
        const otherMass = Math.max(other.size, 10);
        if (draggedMass > otherMass) {
          const massDiff = draggedMass - otherMass;
          const influenceRadius = draggedMass * 14;
          const dx = other.x - dragged.x;
          const dy = other.y - dragged.y;
          const dist = Math.hypot(dx, dy) || 1;
          if (dist < influenceRadius) {
            const springK = 0.012 * (massDiff / draggedMass);
            const damping = 0.85;
            other.vx = other.vx * damping + state.drag.dx * springK * (1 - dist / influenceRadius);
            other.vy = other.vy * damping + state.drag.dy * springK * (1 - dist / influenceRadius);
          }
        }
      }
    }
  }

  // 4. Spring de âncora + onda orgânica: o nó oscila ao redor do home e sempre
  // retorna — sem deriva acumulada. (Substitui a gravidade central, que
  // arrastava todo mundo pra origem com o tempo.)
  for (const nd of visible) {
    if (nd.locked || !linked.has(nd.id)) continue;
    if (state.drag && nd.id === state.drag.id) continue;
    const hx = nd.homeX ?? nd.x;
    const hy = nd.homeY ?? nd.y;
    nd.vx += (hx - nd.x) * config.anchorK;
    nd.vy += (hy - nd.y) * config.anchorK;
    const wavePhase = state.time * 0.18 + hx * 0.003 + hy * 0.003;
    nd.vx += Math.sin(wavePhase) * 0.012;
    nd.vy += Math.cos(wavePhase * 0.7) * 0.009;
  }

  // 5. Damping + integration + soft boundary.
  for (const nd of visible) {
    if (state.drag && nd.id === state.drag.id) {
      nd.vx = 0;
      nd.vy = 0;
      // Home acompanha o drag: ao soltar, o nó fica onde o usuário deixou.
      nd.homeX = nd.x;
      nd.homeY = nd.y;
      nd.prevX = nd.x;
      nd.prevY = nd.y;
      continue;
    }
    if (nd.locked || !linked.has(nd.id)) {
      nd.vx = 0;
      nd.vy = 0;
      continue;
    }
    const distCenter = Math.hypot(nd.x, nd.y);
    const damp = distCenter > config.damping.threshold ? config.damping.far : config.damping.near;
    nd.vx *= damp;
    nd.vy *= damp;
    const maxV = distCenter > config.maxVelocity.threshold ? config.maxVelocity.far : config.maxVelocity.near;
    const speed = Math.hypot(nd.vx, nd.vy);
    if (speed > maxV) {
      nd.vx = (nd.vx / speed) * maxV;
      nd.vy = (nd.vy / speed) * maxV;
    }
    nd.x += nd.vx;
    nd.y += nd.vy;
    const B = config.boundary;
    const overflowX = Math.abs(nd.x) - B;
    const overflowY = Math.abs(nd.y) - B;
    if (overflowX > 0) nd.vx -= Math.sign(nd.x) * overflowX * 0.005;
    if (overflowY > 0) nd.vy -= Math.sign(nd.y) * overflowY * 0.005;
    // Rastro do tick: usado pra detectar teleporte externo no próximo frame.
    nd.prevX = nd.x;
    nd.prevY = nd.y;
  }
}
